"use client"

import React from "react"

/**
 * SfSwitch — the single toggle used for every ON/OFF control in the app.
 * Change dimensions/colors here and every switch everywhere follows.
 *
 * Color rules (do not reverse):
 *   ON  → green (#16A34A) track + white thumb on the right
 *   OFF → red   (#DC2626) track + white thumb on the left
 *   Never gray for OFF. Color always matches the real state.
 *
 * Sizes
 *   md (default): 44×26 track, 22 thumb
 *   sm:           36×22 track, 18 thumb
 */
export const SF_SWITCH_ON = "#16A34A"
export const SF_SWITCH_OFF = "#DC2626"

export default function SfSwitch({
  checked = false,
  onChange,
  disabled = false,
  size = "md",
  showLabel = false,
  label,
  labelOn = "ON",
  labelOff = "OFF",
  ariaLabel,
  className = "",
}) {
  const isSm = size === "sm"
  const trackW = isSm ? 36 : 44
  const trackH = isSm ? 22 : 26
  const thumb = isSm ? 18 : 22
  const pad = 2
  const travel = trackW - thumb - pad * 2

  const trackColor = checked ? SF_SWITCH_ON : SF_SWITCH_OFF
  const stateLabel = checked ? labelOn : labelOff
  const stateColor = checked ? "text-green-700" : "text-red-700"

  const handleToggle = (e) => {
    e?.stopPropagation?.()
    if (disabled) return
    onChange?.(!checked)
  }

  const handleKey = (e) => {
    if (disabled) return
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault()
      onChange?.(!checked)
    }
  }

  // NOTE: do not put Tailwind `rounded-full` on this <button>.
  // index.css forces white bg + gray border on button[class*="rounded-full"].
  // Colors / radius live in .sf-switch CSS (+ aria-checked) so they win.
  return (
    <span className={`inline-flex items-center gap-2 align-middle ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={ariaLabel || label || (checked ? labelOn : labelOff)}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKey}
        className={`sf-switch relative inline-flex items-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-safe:duration-200 ${
          checked ? "focus-visible:ring-green-500/50" : "focus-visible:ring-red-500/50"
        } ${disabled ? "is-disabled" : ""}`}
        style={{
          width: trackW,
          height: trackH,
          backgroundColor: trackColor,
        }}
        data-state={checked ? "on" : "off"}
      >
        <span
          aria-hidden
          className="sf-switch-thumb pointer-events-none absolute bg-white motion-safe:transition-transform motion-safe:duration-200"
          style={{
            width: thumb,
            height: thumb,
            top: pad,
            left: pad,
            transform: `translateX(${checked ? travel : 0}px)`,
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      </button>
      {showLabel && (
        <span
          className={`text-[11px] font-semibold tracking-wide select-none ${stateColor}`}
          style={{ fontFamily: "var(--sf-font-ui, system-ui)" }}
        >
          {stateLabel}
        </span>
      )}
      {label && !showLabel && (
        <span
          className={`text-[13px] text-[var(--sf-ink,#0f172a)] select-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={handleToggle}
        >
          {label}
        </span>
      )}
    </span>
  )
}
